import React from 'react';
import styled from 'styled-components';
import { ExternalLink } from '@studio/legacy-components';
import GuideStep from 'components/InstallationGuide/GuideStep';
import GuideHeading from 'components/InstallationGuide/GuideHeading';
import GuideParagraph from 'components/InstallationGuide/GuideParagraph';
import CopyableCodeSnippet from 'components/Common/CopyableCodeSnippet';
import {
  PAGE_CHANGE_SNIPPETS,
  HTML,
  ANGULAR,
  REACT,
  EMBER,
  VUE,
} from 'constants/installation/snippets';

const SPA_HELP_DOC =
  'https://docs.appcues.com/article/389-single-page-applications-spas';

const UnorderedList = styled.ul`
  margin-left: 16px;
`;

const OrderedList = styled.ol`
  margin-left: 32px;

  ${UnorderedList} {
    margin-top: 12px;
  }
`;

const ListItem = styled.li`
  margin-bottom: 12px;
`;

const isSinglePageFramework = framework =>
  framework === ANGULAR ||
  framework === REACT ||
  framework === EMBER ||
  framework === VUE;

const InstallPageCall = props => {
  const {
    moveForward,
    moveBackward,
    selectedInstallFramework,
    stepMax,
    stepIndex,
  } = props;

  const pageChangeSnippet =
    PAGE_CHANGE_SNIPPETS[selectedInstallFramework || HTML];

  const isSinglePage = isSinglePageFramework(selectedInstallFramework);

  return (
    <GuideStep
      stepIndex={stepIndex}
      stepMax={stepMax}
      title="Re-initialize Appcues upon page change (OPTIONAL)"
      moveForward={moveForward}
      moveBackward={moveBackward}
    >
      <GuideHeading>WHY</GuideHeading>
      <>
        {isSinglePage ? (
          <GuideParagraph>
            Single-page Applications (SPAs) only trigger a full page load when
            they are first loaded. Otherwise, navigating across pages in your
            app doesn't trigger a page load.
          </GuideParagraph>
        ) : (
          <GuideParagraph>
            If you change the URL or page with Javascript within your app, you
            need to tell Appcues that the page has changed. Otherwise you can
            skip this step.
          </GuideParagraph>
        )}
        <GuideParagraph>
          There two ways Appcues can be notified of these page changes, so that
          Appcues can determine the correct content to show to the end-user:
        </GuideParagraph>
        <OrderedList>
          <ListItem>
            Appcues can automatically detect page changes (preferred).
            <UnorderedList>
              <ListItem>
                This is already enabled if you followed the directions in the
                previous step to set <code>enableURLDetection</code> to true.
              </ListItem>
              <ListItem>
                You can move on to step 5 if you’re following this approach.
              </ListItem>
              <ListItem>
                There may be some{' '}
                <ExternalLink
                  icon={false}
                  href={`${SPA_HELP_DOC}#special-cases`}
                >
                  cases where automatic page change detection is not desired
                </ExternalLink>
                . If you would like to disable this, set the{' '}
                <code>enableURLDetection</code> setting to false in the snippet
                from step 3. Below are instructions on how to install without
                automatic page change detection.
              </ListItem>
            </UnorderedList>
          </ListItem>
          <ListItem>
            Appcues can be manually notified of page changes via the{' '}
            <code>Appcues.page()</code> call.
            <UnorderedList>
              <ListItem>
                If this is the option you’d prefer to do, then review the info
                below
              </ListItem>
              <ListItem>
                Also check out more information here under the section{' '}
                <ExternalLink icon={false} href={SPA_HELP_DOC}>
                  Manually notifying Appcues of page changes
                </ExternalLink>
              </ListItem>
            </UnorderedList>
          </ListItem>
        </OrderedList>
      </>
      <GuideHeading>HOW</GuideHeading>
      <GuideParagraph>
        Again, only follow these directions if you’re choosing to move forward
        with option 2 from above. Otherwise you can skip to step 5 now.
      </GuideParagraph>
      {isSinglePage ? (
        <GuideParagraph>
          You&apos;ll need to call <code>Appcues.page()</code> after each URL
          change. This is typically done in your application&apos;s router and
          is handled after a successful page change.
        </GuideParagraph>
      ) : (
        <GuideParagraph>
          Make the <code>Appcues.page()</code> call whenever you use JavaScript
          to change a significant amount of the page.
        </GuideParagraph>
      )}
      {isSinglePage && (
        <CopyableCodeSnippet>{pageChangeSnippet}</CopyableCodeSnippet>
      )}
    </GuideStep>
  );
};

export default InstallPageCall;
